/**
 * useMatching Hooks - React hooks for matching system
 * Phase 5 Part 1: Intelligent Matching System
 * Provides easy access to match data, history, and real-time updates
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import matchingService, {
  MATCH_QUALITY_LABELS,
  RECOMMENDED_ACTIONS,
  TIMING_LABELS,
  SCORE_WEIGHTS
} from '../services/matchingService';

/**
 * Hook for managing seller matches with filtering and pagination
 * @param {string} sellerId - Seller ID
 * @param {Object} options - Hook options
 */
export function useSellerMatches(sellerId, {
  autoFetch = true,
  realtime = true,
  limit = 50,
  minScore = 0
} = {}) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    quality: null,
    sortBy: 'score',
    sortOrder: 'desc'
  });
  const subscriptionRef = useRef(null);

  // Fetch matches
  const fetchMatches = useCallback(async (options = {}) => {
    if (!sellerId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await matchingService.getSellerMatches(sellerId, {
        limit,
        minScore,
        ...filters,
        ...options
      });
      setMatches(result.matches);
      setTotalCount(result.count);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId, limit, minScore, filters]);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Filter by quality
  const filterByQuality = useCallback((quality) => {
    updateFilters({ quality });
  }, [updateFilters]);

  // Sort matches
  const sortMatches = useCallback((sortBy, sortOrder = 'desc') => {
    updateFilters({ sortBy, sortOrder });
  }, [updateFilters]);

  // Handle real-time updates
  useEffect(() => {
    if (autoFetch && sellerId) {
      fetchMatches();
    }

    if (realtime && sellerId) {
      subscriptionRef.current = matchingService.subscribeToMatchUpdates(sellerId, ({ event, match }) => {
        if (event === 'INSERT') {
          setMatches(prev => [match, ...prev]);
          setTotalCount(prev => prev + 1);
        } else if (event === 'UPDATE') {
          setMatches(prev => prev.map(m => m.id === match.id ? match : m));
        } else if (event === 'DELETE') {
          setMatches(prev => prev.filter(m => m.id !== match.id));
          setTotalCount(prev => prev - 1);
        }
      });
    }

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [sellerId, autoFetch, realtime, fetchMatches]);

  // Re-fetch when filters change
  useEffect(() => {
    if (sellerId) {
      fetchMatches();
    }
  }, [filters, fetchMatches, sellerId]);

  // Computed values
  const stats = useMemo(() => {
    if (!matches.length) return null;

    return {
      total: matches.length,
      avgScore: Math.round(matches.reduce((sum, m) => sum + (m.final_score || 0), 0) / matches.length),
      excellent: matches.filter(m => m.match_quality === 'excellent').length,
      strong: matches.filter(m => m.match_quality === 'strong').length,
      improving: matches.filter(m => m.trendInfo?.direction === 'improving').length
    };
  }, [matches]);

  return {
    matches,
    loading,
    error,
    totalCount,
    stats,
    filters,
    updateFilters,
    filterByQuality,
    sortMatches,
    fetchMatches,
    refresh: fetchMatches
  };
}

/**
 * Hook for managing buyer matches
 * @param {string} buyerId - Buyer ID
 * @param {Object} options - Hook options
 */
export function useBuyerMatches(buyerId, {
  autoFetch = true,
  limit = 50,
  minScore = 0
} = {}) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchMatches = useCallback(async (options = {}) => {
    if (!buyerId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await matchingService.getBuyerMatches(buyerId, {
        limit,
        minScore,
        ...options
      });
      setMatches(result.matches);
      setTotalCount(result.count);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buyerId, limit, minScore]);

  useEffect(() => {
    if (autoFetch && buyerId) {
      fetchMatches();
    }
  }, [autoFetch, buyerId, fetchMatches]);

  return {
    matches,
    loading,
    error,
    totalCount,
    fetchMatches,
    refresh: fetchMatches
  };
}

/**
 * Hook for getting top matches for a seller
 * @param {string} sellerId - Seller ID
 * @param {number} limit - Max matches to fetch
 */
export function useTopMatches(sellerId, limit = 10) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTopMatches = useCallback(async () => {
    if (!sellerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getTopMatches(sellerId, limit);
      setMatches(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId, limit]);

  useEffect(() => {
    fetchTopMatches();
  }, [fetchTopMatches]);

  return {
    matches,
    loading,
    error,
    refresh: fetchTopMatches
  };
}

/**
 * Hook for detailed match information between a seller and buyer
 * @param {string} sellerId - Seller ID
 * @param {string} buyerId - Buyer ID
 */
export function useMatchDetails(sellerId, buyerId) {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetails = useCallback(async () => {
    if (!sellerId || !buyerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getMatchDetails(sellerId, buyerId);
      setMatch(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId, buyerId]);

  // Recalculate match
  const recalculate = useCallback(async (triggerEvent = 'manual') => {
    if (!sellerId || !buyerId) return;

    try {
      await matchingService.recalculateMatch(sellerId, buyerId, triggerEvent);
      await fetchDetails(); // Refresh after recalculation
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [sellerId, buyerId, fetchDetails]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  return {
    match,
    loading,
    error,
    recalculate,
    refresh: fetchDetails
  };
}

/**
 * Hook for match history tracking
 * @param {string} sellerId - Seller ID
 * @param {string} buyerId - Buyer ID
 * @param {number} limit - Max history entries
 */
export function useMatchHistory(sellerId, buyerId, limit = 30) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!sellerId || !buyerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getMatchHistory(sellerId, buyerId, limit);
      setHistory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId, buyerId, limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Compute trend data for charts
  const chartData = useMemo(() => {
    if (!history.length) return [];

    return history
      .slice()
      .reverse()
      .map(h => ({
        date: new Date(h.recorded_at).toLocaleDateString(),
        score: h.score,
        timestamp: h.recorded_at
      }));
  }, [history]);

  return {
    history,
    chartData,
    loading,
    error,
    refresh: fetchHistory
  };
}

/**
 * Hook for match explanation
 * @param {string} sellerId - Seller ID
 * @param {string} buyerId - Buyer ID
 */
export function useMatchExplanation(sellerId, buyerId) {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchExplanation = useCallback(async () => {
    if (!sellerId || !buyerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getMatchExplanation(sellerId, buyerId);
      setExplanation(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId, buyerId]);

  useEffect(() => {
    fetchExplanation();
  }, [fetchExplanation]);

  return {
    explanation,
    loading,
    error,
    refresh: fetchExplanation
  };
}

/**
 * Hook for match score trends
 * @param {string} sellerId - Seller ID
 * @param {string} buyerId - Buyer ID
 * @param {number} days - Number of days to look back
 */
export function useMatchScoreTrend(sellerId, buyerId, days = 30) {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrend = useCallback(async () => {
    if (!sellerId || !buyerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getMatchScoreTrend(sellerId, buyerId, days);
      setTrend(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId, buyerId, days]);

  useEffect(() => {
    fetchTrend();
  }, [fetchTrend]);

  return {
    trend,
    loading,
    error,
    refresh: fetchTrend
  };
}

/**
 * Hook for hot matches (high score + high activity)
 * @param {string} sellerId - Seller ID
 * @param {number} limit - Max matches
 */
export function useHotMatches(sellerId, limit = 10) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHotMatches = useCallback(async () => {
    if (!sellerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getHotMatches(sellerId, limit);
      setMatches(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId, limit]);

  useEffect(() => {
    fetchHotMatches();
  }, [fetchHotMatches]);

  return {
    matches,
    loading,
    error,
    refresh: fetchHotMatches
  };
}

/**
 * Hook for matching statistics
 * @param {string} sellerId - Seller ID
 */
export function useMatchingStats(sellerId) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!sellerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getMatchingStats(sellerId);
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats
  };
}

/**
 * Hook for comparable deals
 * @param {string} sellerId - Seller ID
 * @param {number} limit - Max deals
 */
export function useComparableDeals(sellerId, limit = 10) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDeals = useCallback(async () => {
    if (!sellerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getComparableDeals(sellerId, limit);
      setDeals(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId, limit]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  return {
    deals,
    loading,
    error,
    refresh: fetchDeals
  };
}

/**
 * Hook for match trends view
 * @param {string} sellerId - Seller ID
 */
export function useMatchTrends(sellerId) {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrends = useCallback(async () => {
    if (!sellerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getMatchTrends(sellerId);
      setTrends(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  // Categorize trends
  const categorized = useMemo(() => {
    return {
      improving: trends.filter(t => t.trend_category?.includes('improvement') || t.trend_category === 'improving'),
      declining: trends.filter(t => t.trend_category?.includes('decline') || t.trend_category === 'declining'),
      stable: trends.filter(t => t.trend_category === 'stable')
    };
  }, [trends]);

  return {
    trends,
    categorized,
    loading,
    error,
    refresh: fetchTrends
  };
}

/**
 * Hook for buyer matching criteria
 * @param {string} buyerId - Buyer ID
 */
export function useBuyerCriteria(buyerId) {
  const [criteria, setCriteria] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchCriteria = useCallback(async () => {
    if (!buyerId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await matchingService.getBuyerCriteria(buyerId);
      setCriteria(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  const updateCriteria = useCallback(async (updates) => {
    if (!buyerId) return false;

    setSaving(true);
    setError(null);
    try {
      const data = await matchingService.updateBuyerCriteria(buyerId, updates);
      setCriteria(data);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [buyerId]);

  useEffect(() => {
    fetchCriteria();
  }, [fetchCriteria]);

  return {
    criteria,
    loading,
    saving,
    error,
    updateCriteria,
    refresh: fetchCriteria
  };
}

/**
 * Hook for batch recalculation of matches
 * @param {string} sellerId - Seller ID
 */
export function useBatchRecalculate(sellerId) {
  const [recalculating, setRecalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const recalculateAll = useCallback(async () => {
    if (!sellerId) return 0;

    setRecalculating(true);
    setError(null);
    try {
      const count = await matchingService.batchRecalculateSellerMatches(sellerId);
      setResult(count);
      return count;
    } catch (err) {
      setError(err.message);
      return 0;
    } finally {
      setRecalculating(false);
    }
  }, [sellerId]);

  return {
    recalculating,
    result,
    error,
    recalculateAll
  };
}

/**
 * Hook for real-time match updates with notifications
 * @param {string} sellerId - Seller ID
 * @param {Object} options - Hook options
 */
export function useRealtimeMatches(sellerId, {
  onMatchUpdate = null,
  onNewMatch = null,
  onScoreChange = null
} = {}) {
  const [latestUpdate, setLatestUpdate] = useState(null);
  const [updateQueue, setUpdateQueue] = useState([]);
  const subscriptionRef = useRef(null);

  useEffect(() => {
    if (!sellerId) return;

    subscriptionRef.current = matchingService.subscribeToMatchUpdates(sellerId, ({ event, match }) => {
      setLatestUpdate({ event, match, timestamp: new Date() });
      setUpdateQueue(prev => [{ event, match }, ...prev].slice(0, 20));

      if (event === 'INSERT' && onNewMatch) {
        onNewMatch(match);
      } else if (event === 'UPDATE') {
        if (onMatchUpdate) {
          onMatchUpdate(match);
        }
        if (onScoreChange && match.score_change) {
          onScoreChange(match, match.score_change);
        }
      }
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [sellerId, onMatchUpdate, onNewMatch, onScoreChange]);

  const clearQueue = useCallback(() => {
    setUpdateQueue([]);
    setLatestUpdate(null);
  }, []);

  return {
    latestUpdate,
    updateQueue,
    clearQueue
  };
}

// Export constants for use in components
export {
  MATCH_QUALITY_LABELS,
  RECOMMENDED_ACTIONS,
  TIMING_LABELS,
  SCORE_WEIGHTS
};

// Default export
export default useSellerMatches;
